import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * One error boundary per widget. CLAUDE.md constraint 4: no widget may white-screen the page.
 *
 * **This is the documented exception to the function-components-only rule.** React has no
 * hook equivalent of `getDerivedStateFromError` / `componentDidCatch`; a class is the only
 * way to catch a render error, and pulling in `react-error-boundary` for forty lines of
 * built-in API would add a dependency to a bundle that runs unattended on a wall.
 *
 * ## No automatic retry
 *
 * A crashed widget stays crashed until the page reloads, deliberately. Widgets crash on the
 * data they are given, and the data is a retained MQTT message that arrives again on the
 * next reconnect — a boundary that re-mounted on error would crash, re-mount, and crash
 * again in a loop, pinning the CPU of a display nobody is looking at. The recovery path is
 * the `refresh` topic (SYM-489), which reloads the page.
 *
 * ## What it does not catch
 *
 * React error boundaries catch errors thrown during render, in lifecycle methods and in
 * constructors of the tree below them. They do **not** catch throws inside event handlers,
 * `setTimeout`/`setInterval` callbacks, or promise rejections — which in this app means a
 * throw inside a widget's own interval tick is still uncaught. Widgets keep their `try`
 * blocks where they touch external data; this is the backstop, not the strategy.
 */

type WidgetBoundaryProps = {
  /** Config `type`, for the log line — the only place the crashed widget can still be named. */
  type: string;
  componentId: string;
  children: ReactNode;
};

type WidgetBoundaryState = {
  failed: boolean;
};

export class WidgetBoundary extends Component<WidgetBoundaryProps, WidgetBoundaryState> {
  override state: WidgetBoundaryState = { failed: false };

  static getDerivedStateFromError(): WidgetBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Displays are debugged over a remote console, so everything needed to identify the
    // widget goes in one line: the config type, the store key its data lives under, and the
    // component stack that says which child actually threw.
    console.error('[signage] widget crashed', {
      type: this.props.type,
      componentId: this.props.componentId,
      error,
      componentStack: info.componentStack,
    });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    // Muted, same box, no colour of its own — a crashed widget should be obvious to someone
    // looking for it and unremarkable to someone walking past the screen.
    return (
      <div
        className="w-full p-4 text-center text-sm opacity-40"
        data-widget-crashed={this.props.type}
      >
        {this.props.type} unavailable
      </div>
    );
  }
}
